// Copyright (c) 2026 AesirX.io Ltd. All rights reserved.
// This source code is proprietary and confidential.
// See LICENSE.md for licensing terms.
/**
 * Where a retention policy is in its own life — Sprint 65.11.0.
 *
 * `RetentionPolicy` carried **no state at all**: `_id`, `retention_id`,
 * `org_id`, `name`, two ids, a period, a justification, a review frequency, a
 * disposal method, a department and two timestamps. So LT-01 raised a duty and
 * nothing could close it, because `AdoptedProofSource` needs a field holding
 * the record's own account of its state and naming any of the above would have
 * read a value that is not a state. The register in
 * `every-fact-can-be-closed-65.7.0.test.ts` carried the row with exactly that
 * reason, and named `data-mapping` as its owner.
 *
 * Owned by the module that owns the record, as that register said it must be.
 *
 * `superseded` is separate from `retired` on purpose: a policy replaced by a
 * newer one and a policy withdrawn without replacement leave the organisation
 * in different positions, and a single "inactive" would hide which.
 */
export const RETENTION_POLICY_STATES = ['draft', 'active', 'superseded', 'retired'];
/*
 * Sprint 64.73.0 — `DataTier` is retired. There is no union here any more.
 *
 * ## What it was, and why a type could not carry it
 *
 * `export type DataTier = 'regular' | 'important' | 'core'` was added by Sprint
 * 46.1.0. Those three values are **Quyết định 20/2025/QĐ-TTg's** vocabulary
 * (*dữ liệu quan trọng*, *dữ liệu cốt lõi*), under Luật Dữ liệu 60/2024/QH15. A
 * tenant in Germany has neither, so a shared platform type asked every tenant a
 * Vietnamese question — R-46's exact subject: the pack declares the vocabulary,
 * the platform discovers it.
 *
 * Sprint 62.4.0 replaced the *decision* with a scheme-scoped screening a person
 * reviews. Sprint 64.10.0 made that screening the answer for `DataRegistryItem`
 * — read a subject's tier through `resolveClassifiedTier` (`utils/classified-tier.ts`)
 * — and demoted the union without deleting it, naming five survivors and one
 * reason for all of them: *blocked on idea-catalog #127*.
 *
 * ## What this sprint measured, and what changed
 *
 * The union occupied **14 type-level references in 6 files** — the TypeScript
 * language service's `findReferences` over a program that maps `@vn-grc/shared`
 * onto its source, not a grep (grep reports 39 lines in 17 files, 25 of them
 * prose in docblocks and tests). Two of the three type positions typed a value
 * a **regulatory pack declares**, not one the platform chooses:
 *
 * - `ClassificationTier.platform_data_tier` — a field inside a pack's
 *   `frameworks.json`. Typing it with this union meant a non-Vietnamese pack
 *   could not declare a correspondence except in Vietnamese. Measured across
 *   `packs/`: **13 declarations, 10 of them `null`** (the two vn-cybersecurity
 *   `information_system` schemes, five levels each) and **3 non-null, every one
 *   of them the identity map** (`regular→regular`, `important→important`,
 *   `core→core`) on the single `data_asset` scheme. The union carried no
 *   information the pack did not already state.
 * - `DataRegistryItem.data_tier` (and its draft) — the mirror, whose
 *   authoritative writer is `classification-service.reconcileLegacyDataTier`,
 *   which writes `tier?.platform_data_tier ?? null` — again a pack-declared
 *   value — and whose authoritative reader `resolveClassifiedTier` already took
 *   `string | null | undefined`.
 *
 * Both are now `string`. The third position is `ProcessingActivity.data_tier`
 * below, which is **retained** — see its own comment for the new reason.
 *
 * No value moved. AC4 binds: a recorded `core` keeps its value and its
 * `manual_legacy` source, and this sprint ships no migration.
 */
/**
 * The three tier codes the platform used before any pack declared a
 * `data_asset` scheme — **a compatibility floor, not a vocabulary.**
 *
 * Retiring `DataTier` deleted the type but not the values: they are still what
 * a tenant with no `data_asset` scheme installed is offered, and still what
 * every `data_tier` recorded before Sprint 62.4.0 holds. Exactly ONE scheme in
 * the whole `packs/` corpus is `data_asset`-scoped, so "no scheme installed" is
 * the ordinary case, not the edge.
 *
 * Named here, once, because gate E12.5 pass 2 (F1) found the frontend and the
 * backend disagreeing about it while both spelled it out: `ActivitiesPage`
 * offered these three as its fallback and `assertTierCodeWritable` refused all
 * three, so the operator could neither create nor update. Two transcriptions of
 * one list, drifting — the defect shape Sprint 64.73.0 exists to remove.
 *
 * NOT for widening. A tenant WITH a declared scheme is held to that scheme;
 * these are the floor beneath tenants the packs have not reached. Do not read
 * this constant as an allowed-values list anywhere a scheme is present, and do
 * not add to it — a fourth value here would be a platform-level regulatory
 * vocabulary, which is R-46's subject.
 *
 * ## R-46, stated rather than assumed (gate E12.5 pass 3, F10)
 *
 * These three strings ARE a country's vocabulary: `classified-tier.ts` records
 * them as Quyết định 20/2025/QĐ-TTg's, and R-46 names "country-specific tier
 * vocabulary" explicitly. So a platform-level constant holding them is a
 * governance call, and it is made here in the open rather than left implicit:
 *
 * - They are already the platform's de-facto floor. Every `data_tier` recorded
 *   before Sprint 62.4.0 holds one, on tenants in every jurisdiction, and the
 *   activity form has offered them to everyone since long before this file.
 *   Naming them does not create the coupling; it makes it findable.
 * - The alternative is worse in the direction that matters. The two consumers
 *   held separate copies, drifted, and produced a hard 400 on create and
 *   update for nearly every tenant. A wrong thing in one place is a smaller
 *   defect than the same wrong thing in two places disagreeing.
 * - `lint:framework-hardcoding` **now scans this file** and finds nothing here.
 *   Sprint 64.43.0 added `src/shared/src` as a third scan root twelve days
 *   after this paragraph was written, which falsified its first clause; the
 *   conclusion is unchanged and the reason is now measured rather than assumed.
 *   These three strings are lowercase English words — not a corpus framework
 *   code (`[A-Z0-9_]+`), not an instrument number, not `Word + digit` — so no
 *   pattern matches them, and the file is on neither the allowlist nor the
 *   residue register. 64.43.0 pins that with a named test, so a future match
 *   forces the decision to be retaken in the open rather than absorbed.
 * - **No tier-vocabulary pattern exists, deliberately.** 64.43.0 declined to add
 *   one, and the reason is the same one this constant's own docblock is about:
 *   writing `regular|important|core` into `lint-framework-hardcoding.mjs` would
 *   put one country's vocabulary inside the check that exists to stop the
 *   platform assuming that country. So **no check is watching these three
 *   strings**, which is why the constraint is written down here instead.
 *
 * The real exit is a pack that declares a `data_asset` scheme for every tenant,
 * at which point `declared.size === 0` stops being the ordinary case and this
 * constant stops being reachable. That is the direction; this is the floor
 * until then.
 */
export const LEGACY_TIER_CODES = ['regular', 'important', 'core'];
export const PERSONAL_DATA_SCOPES = ['personal', 'non_personal', 'mixed', 'unknown'];
/* ── Evidence Items (Epic E) ─────────────────────────────── */
/** fix-sprint-20261004-63-8-2 F4 — the evidence types the data-mapping Evidence page offers. */
export const EVIDENCE_TYPES = [
    'document',
    'screenshot',
    'policy',
    'contract',
    'assessment',
    'correspondence',
    'other',
];
/* ── Attestation Cycles (Sprint 20.5.0 — Epic 1) ──────────── */
/**
 * fix-sprint-20261004-63-8-2 F4 — the scopes an attestation cycle may carry: the six the cycle page
 * offers (`purposes` and `retention` were sent and not named here) and the
 * item-level `registry_items` (Sprint 45.4.0). The create route reads this list.
 */
export const ATTESTATION_SCOPES = [
    'systems',
    'flows',
    'activities',
    'categories',
    'purposes',
    'retention',
    'registry_items',
];
//# sourceMappingURL=data-mapping.js.map